import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { Card, CardContent, CardFooter } from "../atoms/card.js";
import { cx } from "../internal/cx.js";

const registrationFormCardVariants = cva("cw-v2-registration-form-card", {
  variants: {
    variant: {
      default: "cw-v2-registration-form-card--default",
      elevated: "cw-v2-registration-form-card--elevated",
      activation: "cw-v2-registration-form-card--activation",
      onboarding: "cw-v2-registration-form-card--onboarding",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export type RegistrationFormCardVariant = NonNullable<
  VariantProps<typeof registrationFormCardVariants>["variant"]
>;

export interface RegistrationFormCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof registrationFormCardVariants> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
}

/**
 * Registration / activation / onboarding form chrome. Wraps the inner form
 * in a `<Card>` with an optional eyebrow, title, description, and a footer
 * action row. Drives `Card`'s `elevated` from the variant — anything but
 * `default` floats over the page.
 */
export const RegistrationFormCard = React.forwardRef<HTMLDivElement, RegistrationFormCardProps>(
  function RegistrationFormCard(
    { actions, children, className, description, eyebrow, title, variant, ...props },
    ref,
  ) {
    return (
      <Card
        ref={ref as React.Ref<HTMLElement>}
        className={cx(registrationFormCardVariants({ variant }), className)}
        elevated={variant === "elevated" || variant === "activation" || variant === "onboarding"}
        {...props}
      >
        <CardContent>
          {eyebrow || title || description ? (
            <header className="cw-v2-registration-form-card__header">
              {eyebrow ? <p className="cw-v2-registration-form-card__eyebrow">{eyebrow}</p> : null}
              {title ? <h2 className="cw-v2-registration-form-card__title">{title}</h2> : null}
              {description ? (
                <p className="cw-v2-registration-form-card__description">{description}</p>
              ) : null}
            </header>
          ) : null}
          <div className="cw-v2-registration-form-card__body">{children}</div>
        </CardContent>
        {actions ? (
          <CardFooter className="cw-v2-registration-form-card__actions">{actions}</CardFooter>
        ) : null}
      </Card>
    );
  },
);
RegistrationFormCard.displayName = "RegistrationFormCard";
